import { Delete } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Button, Grid, IconButton, Radio, RadioGroup, Stack, TextField } from '@mui/material';
import clsx from 'clsx';
import { FormikValues } from 'formik';
import { get, map } from 'lodash';
import { ComponentType, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QUESTION_LANGUAGES } from 'src/common/lang-constants';
import { DirectionsSelect } from 'src/components/directions';
import { CheckedIcon, UncheckedIcon } from 'src/components/icons';
import { OrganizationsSelect } from 'src/components/organizations';
import { useAuth, useEdit } from 'src/hooks';
import {
  addQuestion,
  clearQuestion,
  clearQuestionRequest,
  getQuestion,
  updateQuestion,
} from 'src/thunks/question-thunks';
import { alphabetLetterByIndex } from '../../../common/utils/format-utils';
import {
  QuestionsCategoriesSelect,
  QuestionsDifficultyLevelsSelect,
  QuestionsFieldsSelect,
} from '../../questions';
import useStyles from './styles';
import testsEditFormValidationSchema from './tests-edit-form-validation-scheme';

export interface TestsEditFormProps {
  id?: string | number;
  onCancel?: () => void;
  onSubmit: (values: FormikValues) => void;
}

export const TestsEditForm: ComponentType<TestsEditFormProps> = ({ id, onCancel, onSubmit }) => {
  const { t } = useTranslation();
  const { isSuperAdmin, user } = useAuth();
  const classes = useStyles();

  const { formik, hasError, getErrorMessage } = useEdit({
    id,
    entity: 'question',
    initialValues: {
      language: 'uz',
      title: '',
      correctAnswer: 0,
      answers: ['', ''],
      direction: '',
      organization: get(user, ['data', 'organization', 'id'], ''),
      category: '',
      difficultyLevel: '',
      field: '',
    },
    validationSchema: testsEditFormValidationSchema,
    onGetOne: getQuestion,
    onClearOne: clearQuestion,
    onClear: clearQuestionRequest,
    onCreate: addQuestion,
    onEdit: updateQuestion,
    onSubmit: onSubmit,
  });

  const [selected, setSelected] = useState<number | null>(formik.values.correctAnswer);
  const [lang, setLang] = useState('uz');

  useEffect(() => {
    const language = get(formik.values, 'language');
    const correctAnswer = get(formik.values, 'correctAnswer');
    if (language) {
      setLang(language);
    }
    if (correctAnswer !== null) {
      setSelected(correctAnswer);
    }
  }, [formik.values]);

  const handleLangChange = (lang: string) => {
    formik.setFieldValue('language', lang);
  };

  const handleSelectAnswer = (variant: number) => {
    setSelected(variant);
    formik.setFieldValue('correctAnswer', variant);
  };

  const handleFieldChange = (field: string, e: any) => {
    formik.setFieldValue(field, e.target.value);
    formik.setFieldError(field, null);
  };

  const handleAddAnswer = () => {
    const answers = formik.values.answers;
    answers.push('');
    formik.setFieldValue('answers', answers);
  };

  const handleRemoveAnswer = (idx: number) => {
    const answers = formik.values.answers;
    answers.splice(idx, 1);
    formik.setFieldValue('answers', answers);
  };

  const hasAnswersError = (idx: number) => {
    return (
      get(formik, ['touched', 'answers', idx], false) &&
      Boolean(get(formik, ['errors', 'answers', idx], false))
    );
  };

  const getAnswersErrorMessage = (idx: number) => {
    if (!hasAnswersError(idx)) return ' ';
    const key = get(formik, ['errors', 'answers', idx], '');
    if (key.includes('error.')) {
      return t(key, 'Ошибка');
    }
    return key;
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <Stack spacing={3} className={classes.langButtons} direction="row" mb={3}>
        {map(QUESTION_LANGUAGES, (item) => (
          <Button
            onClick={() => handleLangChange(item.code)}
            className={clsx(classes.langButton, {
              [classes.activeLangButton]: item.code === lang,
            })}
            key={item.code}
          >
            {item.label}
          </Button>
        ))}
      </Stack>

      <Grid container columnSpacing={3}>
        {isSuperAdmin && (
          <Grid item xs={6}>
            <OrganizationsSelect
              {...formik.getFieldProps('organization')}
              onChange={(e: any) => handleFieldChange('organization', e)}
              error={hasError('organization')}
              helperText={getErrorMessage('organization')}
              label={t('organizations.name', 'Название организации')}
              placeholder={t('organizations.name.placeholder', 'Введите название организации')}
              // disabled={isLoading || isFetching}
              fullWidth
            />
          </Grid>
        )}
        <Grid item xs={isSuperAdmin ? 6 : 12}>
          <DirectionsSelect
            {...formik.getFieldProps('direction')}
            organization={formik.values.organization}
            onChange={(e: any) => handleFieldChange('direction', e)}
            error={hasError('direction')}
            helperText={getErrorMessage('direction')}
            label={t('direction', 'Направление')}
            fullWidth
          />
        </Grid>
        <Grid item xs={4}>
          <QuestionsDifficultyLevelsSelect
            {...formik.getFieldProps('difficultyLevel')}
            onChange={(e: any) => handleFieldChange('difficultyLevel', e)}
            error={hasError('difficultyLevel')}
            helperText={getErrorMessage('difficultyLevel')}
            label={t('question.difficultyLevel', 'Сложность')}
            fullWidth
          />
        </Grid>
        <Grid item xs={4}>
          <QuestionsCategoriesSelect
            {...formik.getFieldProps('category')}
            onChange={(e: any) => handleFieldChange('category', e)}
            error={hasError('category')}
            helperText={getErrorMessage('category')}
            label={t('question.category', 'Категория')}
            fullWidth
          />
        </Grid>
        <Grid item xs={4}>
          <QuestionsFieldsSelect
            {...formik.getFieldProps('field')}
            onChange={(e: any) => handleFieldChange('field', e)}
            error={hasError('field')}
            helperText={getErrorMessage('field')}
            label={t('question.field', 'Деятельность')}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            {...formik.getFieldProps('title')}
            label={t('question.text', 'Текст вопроса')}
            multiline
            minRows={2}
            placeholder={t('question.placeholder', 'Введите вопрос')}
            error={hasError('title')}
            helperText={getErrorMessage('title')}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <Stack component={RadioGroup}>
            {map(formik.values.answers, (answer, idx: number) => (
              <Stack direction="row" alignItems="flex-end" key={idx}>
                <TextField
                  {...formik.getFieldProps(`answers[${idx}]`)}
                  label={t(
                    'question.answer.variant',
                    `Вариант ответа ${alphabetLetterByIndex(idx)}`,
                    {
                      variant: alphabetLetterByIndex(idx),
                    }
                  )}
                  error={hasAnswersError(idx)}
                  helperText={getAnswersErrorMessage(idx)}
                  placeholder={t('question.answer.placeholder', 'Введите вариант ответа')}
                  fullWidth
                />
                <Radio
                  checked={selected === idx}
                  onChange={() => handleSelectAnswer(idx)}
                  value={idx}
                  name="radio-buttons"
                  inputProps={{ 'aria-label': 'A' }}
                  icon={<UncheckedIcon />}
                  checkedIcon={<CheckedIcon />}
                  className={classes.radioBtn}
                />
                <IconButton
                  onClick={() => handleRemoveAnswer(idx)}
                  className={classes.removeAnswer}
                  color="error"
                  disabled={formik.values.answers.length < 3}
                  title={t('question.removeAnswer', 'Удалить вариант ответа')}
                >
                  <Delete />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Button onClick={handleAddAnswer} variant="contained" color="primary" fullWidth>
            {t('question.addAnswer', 'Добавить вариант ответа')}
          </Button>
        </Grid>
      </Grid>

      <Stack spacing={2} justifyContent="space-between" direction="row" mt={4}>
        {onCancel && (
          <Button
            onClick={onCancel}
            variant="outlined"
            size="large"
            // disabled={isLoading}
          >
            {t('action.cancel', 'Отмена')}
          </Button>
        )}
        <LoadingButton
          // loading={isLoading || isFetching}
          type="submit"
          variant="contained"
          color="secondary"
          size="large"
        >
          {t('action.save', 'Сохранить')}
        </LoadingButton>
      </Stack>
    </form>
  );
};
